import { useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as SearchFilterIcon } from '@/assets/icons/search.filter.icon.svg';
import { ReactComponent as ArrowUpIcon } from '@/assets/icons/arrow-up.icon.svg';
import { ReactComponent as ArrowDownIcon } from '@/assets/icons/arrow-down.icon.svg';
import { ReactComponent as UncheckIcon } from '@/assets/icons/uncheck.icon.svg';
import { ReactComponent as CheckIcon } from '@/assets/icons/check.icon.svg';
import { AnimatePresence, motion } from 'framer-motion';
import useOnClickOutside from '@/hooks/useClickOutside';

export const SearchControl = ({ onSearch, currentFilters, availableFilters, searchExact, onSearchExactChange }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(currentFilters);
  const [tempSearchExact, setTempSearchExact] = useState(searchExact);

  const ref = useRef();
  useOnClickOutside(ref, () => setIsDropdownOpen(false));

  const toggleFilter = (filterValue) => {
    if (tempFilters.includes(filterValue)) {
      setTempFilters(tempFilters.filter((f) => f !== filterValue));
    } else {
      setTempFilters([...tempFilters, filterValue]);
    }
  };

  const handleApplyFilters = () => {
    onSearch(tempFilters, tempSearchExact);
    onSearchExactChange(tempSearchExact);
    setIsDropdownOpen(false);
  };

  const getFilterLabel = (value) => {
    const filter = availableFilters.find((f) => f.value === value);
    return filter?.label || value;
  };

  return (
    <ControlView>
      <div className="filter-display">
        <button
          className="search-filter"
          onClick={() => {
            setIsDropdownOpen(!isDropdownOpen);
            setTempFilters(currentFilters); // Reset temp filters when opening
            setTempSearchExact(searchExact);
          }}>
          <div className="icon-wrapper">
            <SearchFilterIcon className="icon" />
            <div className="arrows">
              {isDropdownOpen ? (
                <ArrowUpIcon className="arrow" />
              ) : (
                <ArrowDownIcon className="arrow" />
              )}
            </div>
          </div>
        </button>
        {currentFilters.length > 0 && (
          <div className="selected-filters">
            {currentFilters.map((filter) => (
              <span key={filter} className="filter-tag">
                {getFilterLabel(filter)}
              </span>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            ref={ref}
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="dropdown-menu">
            <div className="items">
              {/* Search Exact checkbox - always first */}
              <button
                className="filter-items exact-search-item"
                onClick={() => setTempSearchExact(!tempSearchExact)}>
                {tempSearchExact ? <CheckIcon size={10} /> : <UncheckIcon size={10} />}
                <span>Exact Match Only</span>
              </button>

              {/* Separator line */}
              <div className="separator" />
              {availableFilters.map((filter) => (
                <button
                  key={filter.value}
                  className="filter-items"
                  onClick={() => toggleFilter(filter.value)}>
                  {tempFilters.includes(filter.value) ? (
                    <CheckIcon size={10} />
                  ) : (
                    <UncheckIcon size={10} />
                  )}
                  <span>{filter.label}</span>
                </button>
              ))}
            </div>
            <div className="filter-actions">
              <button onClick={handleApplyFilters} className="apply-filter">
                Apply Filter
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ControlView>
  );
};

const ControlView = styled.div`
  position: relative;
  margin: 0 20px;
  max-width: 40px;
  display: inline-flex;
  vertical-align: top;

  .filter-display {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    /* width: auto; */

    .search-filter {
      display: flex;
      /* position: relative; */
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background-color: white;
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      color: ${({ theme }) => theme.colors.secondary};

      .icon-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        color: ${({ theme }) => theme.colors.secondary};

        .icon {
          font-weight: 300;
          size: 19px;
          /* width: 19px; */
          color: ${({ theme }) => theme.colors.secondary};
        }

        .arrows {
          margin: 0 auto;

          .arrow {
            width: 10px;
            height: 10px;
            color: ${({ theme }) => theme.colors.secondary};
          }
        }
      }
    }

    /* .selected-filters {
      display: flex;
      align-items: center;
      gap: 4px;
      width: 300px;
      flex-wrap: wrap;
      border: 1px solid red;
      max-width: 400px;
      overflow: hidden;

      .filter-tag {
        font-size: 0.75rem;
        padding: 2px 8px;
        background: ${({ theme }) => theme.colors.inputBackground};
        border-radius: 4px;
        color: ${({ theme }) => theme.colors.secondary};
        white-space: nowrap;
      }
    } */
    .selected-filters {
      position: absolute;
      left: 100%; /* Position to the right of the filter button */
      top: 0;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      width: auto; /* Let it grow as needed */
      width: 300px; /* Set your maximum desired width */
      padding-left: 8px; /* Spacing from the filter button */
      background: white; /* Match your background */
      z-index: 1; /* Ensure it appears above other elements */

      .filter-tag {
        font-size: 0.75rem;
        padding: 2px 8px;
        background: ${({ theme }) => theme.colors.inputBackground};
        border-radius: 4px;
        color: ${({ theme }) => theme.colors.secondary};
        white-space: nowrap;
      }
    }
  }

  .dropdown-menu {
    position: absolute;
    top: 35px;
    left: 0px;
    z-index: 10;
    margin-top: 0.5rem;
    width: 300px;
    background-color: white;
    border-radius: 10px;
    box-shadow:
      0px 0px 2px rgba(125, 125, 125, 0.1),
      0px 2px 10px rgba(0, 0, 0, 0.1);
    outline: none;

    .items {
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;

      .exact-search-item {
      }

      .separator {
        height: 1px;
        background: ${({ theme }) => theme.colors.line};
        margin: 8px 0;
      }

      .filter-items {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 0.5rem 1rem;
        text-align: left;
        font-size: 0.875rem;
        background-color: white;
        border: none;
        border-radius: 10px;

        &:hover {
          background-color: #f3f4f6; /* gray-100 */
        }
      }
    }

    .filter-actions {
      padding: 0.75rem;
      border-top: 1px solid ${({ theme }) => theme.colors.line};

      .apply-filter {
        width: 100%;
        padding: 0.5rem;
        background-color: ${({ theme }) => theme.colors.secondary};
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease-in-out;

        &:hover {
          opacity: 0.9;
        }
      }
    }
  }
`;
